import { useRef, useCallback, useEffect, type RefObject } from 'react';

interface UseDragScrollOptions {
    /** Sensitivity multiplier for scroll speed (default: 1) */
    sensitivity?: number;
    /** Optional external ref to use instead of creating a new one */
    externalRef?: RefObject<HTMLElement>;
}

interface UseDragScrollReturn {
    /** Ref to attach to the scrollable container */
    scrollRef: RefObject<HTMLElement>;
    /** Whether the user is currently dragging */
    isDragging: boolean;
}

/**
 * Hook that enables horizontal drag-to-scroll with momentum on a container element.
 * Tracks mousedown → mousemove → mouseup to scroll the container via mouse drag.
 * On release, continues scrolling with decelerating momentum for a mobile-like feel.
 * Also prevents click events from firing after a drag to avoid accidental navigation.
 *
 * @param options - Configuration options
 * @returns Object containing the ref to attach and dragging state
 */
export const useDragScroll = (
    options: UseDragScrollOptions = {},
): UseDragScrollReturn => {
    const { sensitivity = 1, externalRef } = options;
    const internalRef = useRef<HTMLElement>(null);
    const scrollRef = externalRef ?? internalRef;
    const isDraggingRef = useRef(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);
    const hasDraggedRef = useRef(false);

    // Momentum tracking
    const velocityRef = useRef(0);
    const lastMoveTimeRef = useRef(0);
    const lastClientXRef = useRef(0);
    const momentumRafRef = useRef<number | null>(null);

    const cancelMomentum = useCallback(() => {
        if (momentumRafRef.current !== null) {
            cancelAnimationFrame(momentumRafRef.current);
            momentumRafRef.current = null;
        }
    }, []);

    const handleMouseDown = useCallback(
        (e: MouseEvent) => {
            const container = scrollRef.current;
            if (!container) return;

            // Stop any ongoing momentum animation
            cancelMomentum();

            isDraggingRef.current = true;
            hasDraggedRef.current = false;
            startXRef.current = e.clientX;
            scrollLeftRef.current = container.scrollLeft;
            velocityRef.current = 0;
            lastMoveTimeRef.current = performance.now();
            lastClientXRef.current = e.clientX;
            container.style.cursor = 'grabbing';
            container.style.userSelect = 'none';
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cancelMomentum],
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDraggingRef.current) return;
            const container = scrollRef.current;
            if (!container) return;

            e.preventDefault();
            const walk = (e.clientX - startXRef.current) * sensitivity;

            // Mark as dragged if moved more than 5px (to distinguish from clicks)
            if (Math.abs(walk) > 5) {
                hasDraggedRef.current = true;
            }

            // Track velocity for momentum (pixels per millisecond)
            const now = performance.now();
            const dt = now - lastMoveTimeRef.current;
            if (dt > 0) {
                velocityRef.current = (e.clientX - lastClientXRef.current) / dt;
            }
            lastMoveTimeRef.current = now;
            lastClientXRef.current = e.clientX;

            container.scrollLeft = scrollLeftRef.current - walk;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sensitivity],
    );

    const handleMouseUp = useCallback(() => {
        const container = scrollRef.current;
        if (container) {
            container.style.cursor = '';
            container.style.userSelect = '';
        }
        isDraggingRef.current = false;

        // Start momentum animation if velocity is significant
        // Convert px/ms to px/frame (~16ms per frame at 60Hz)
        let velocity = velocityRef.current * 10;
        const friction = 0.92;

        const animate = () => {
            const el = scrollRef.current;
            if (!el || Math.abs(velocity) < 0.5) {
                momentumRafRef.current = null;
                return;
            }
            el.scrollLeft -= velocity;
            velocity *= friction;
            momentumRafRef.current = requestAnimationFrame(animate);
        };

        if (Math.abs(velocity) > 0.5) {
            momentumRafRef.current = requestAnimationFrame(animate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMouseLeave = useCallback(() => {
        if (isDraggingRef.current) {
            handleMouseUp();
        }
    }, [handleMouseUp]);

    // Prevent native browser link-drag (drag-to-bookmark) on <a> tags,
    // which intercepts mousemove events and breaks drag-to-scroll
    const handleDragStart = useCallback((e: DragEvent) => {
        e.preventDefault();
    }, []);

    // Prevent click events from firing after a drag
    const handleClick = useCallback((e: MouseEvent) => {
        if (hasDraggedRef.current) {
            e.preventDefault();
            e.stopPropagation();
            hasDraggedRef.current = false;
        }
    }, []);

    useEffect(() => {
        const container = scrollRef.current;
        if (!container) return;

        container.addEventListener('mousedown', handleMouseDown);
        container.addEventListener('mousemove', handleMouseMove);
        container.addEventListener('mouseup', handleMouseUp);
        container.addEventListener('mouseleave', handleMouseLeave);
        container.addEventListener('click', handleClick, true);
        container.addEventListener('dragstart', handleDragStart);

        return () => {
            container.removeEventListener('mousedown', handleMouseDown);
            container.removeEventListener('mousemove', handleMouseMove);
            container.removeEventListener('mouseup', handleMouseUp);
            container.removeEventListener('mouseleave', handleMouseLeave);
            container.removeEventListener('click', handleClick, true);
            container.removeEventListener('dragstart', handleDragStart);
            cancelMomentum();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        handleMouseDown,
        handleMouseMove,
        handleMouseUp,
        handleMouseLeave,
        handleClick,
        handleDragStart,
        cancelMomentum,
    ]);

    return {
        scrollRef,
        isDragging: isDraggingRef.current,
    };
};
